import { useSiteContent } from "@/context/SiteContentContext";
import { useT } from "@/i18n";
import { Instagram, Music2, Mail, Send, Youtube, Twitter, Globe } from "lucide-react";

/**
 * Footer — admin-editable ending.
 * - Optional big title + subtitle (toggle on/off from admin).
 * - Optional extra rich text paragraph.
 * - Location / links (Instagram / SoundCloud / Spotify / YouTube / Twitter / Telegram / Website / Email)
 *   render as small icon+label pills so the row stays scannable.
 */
const SOCIAL_MAP = [
    { key: "ig",         re: /(instagram\.com|@)/i,          label: "instagram",  Icon: Instagram },
    { key: "sc",         re: /soundcloud\.com/i,             label: "soundcloud", Icon: Music2 },
    { key: "spotify",    re: /spotify\.com/i,                label: "spotify",    Icon: Music2 },
    { key: "yt",         re: /(youtube\.com|youtu\.be)/i,    label: "youtube",    Icon: Youtube },
    { key: "tw",         re: /(twitter\.com|x\.com)/i,       label: "twitter",    Icon: Twitter },
    { key: "tg",         re: /(t\.me|telegram)/i,            label: "telegram",   Icon: Send },
    { key: "site",       re: /^https?:\/\//i,                label: "website",    Icon: Globe },
];
function socialFor(url) {
    for (const s of SOCIAL_MAP) if (s.re.test(url)) return s;
    return SOCIAL_MAP[SOCIAL_MAP.length - 1];
}

export const Footer = () => {
    const { content } = useSiteContent();
    const { lang } = useT();

    // Admin-editable big title / subtitle. Both are opt-in — off by default so old
    // deployments stay quiet, but the admin can turn them on and edit per language.
    const showTitle    = content.footer_show_title === "1";
    const showSubtitle = content.footer_show_subtitle === "1";
    const showExtra    = content.footer_show_extra === "1";
    const title    = content[`footer_title_${lang}`]    || content.footer_title_en    || "";
    const subtitle = content[`footer_subtitle_${lang}`] || content.footer_subtitle_en || "";
    const extra    = content[`footer_extra_${lang}`]    || content.footer_extra_en    || "";

    const location = content[`footer_location_${lang}`] || content.footer_location_en || content.footer_location || "";
    const email = content.footer_email || "";
    // Support up to 4 admin-defined links (footer_link_1_url … footer_link_4_url).
    const links = [1, 2, 3, 4]
        .map((n) => content[`footer_link_${n}_url`])
        .filter(Boolean)
        .concat([
            content.footer_ig_url || content.footer_instagram_url || "",
            content.footer_sc_url || content.footer_soundcloud_url || "",
        ].filter(Boolean));

    if (!showTitle && !showSubtitle && !showExtra && !location && !email && links.length === 0) return null;

    return (
        <footer data-testid="site-footer" className="w-full px-4 md:px-8">
            <div className="max-w-6xl mx-auto py-16 md:py-24 border-t border-current/10">
                {showTitle && title && (
                    <h2 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] mb-4" data-testid="footer-title">
                        {title}
                    </h2>
                )}
                {showSubtitle && subtitle && (
                    <p className="font-heading text-lg md:text-2xl leading-snug opacity-80 max-w-2xl mb-8" data-testid="footer-subtitle">
                        {subtitle}
                    </p>
                )}
                {showExtra && extra && (
                    <p className="font-body text-sm md:text-base leading-relaxed opacity-70 max-w-2xl whitespace-pre-line mb-8" data-testid="footer-extra">
                        {extra}
                    </p>
                )}

                <ul className="flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-sm md:text-base uppercase tracking-[0.2em]">
                    {location && (
                        <li className="opacity-80" data-testid="footer-location">
                            {location}
                        </li>
                    )}
                    {links.map((url, i) => {
                        const s = socialFor(url);
                        return (
                            <li key={i}>
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`footer-link-${s.key}-${i}`}
                                    className="inline-flex items-center gap-2 opacity-80 hover:opacity-100 hover:text-[color:var(--sc-accent)] transition-colors"
                                >
                                    <s.Icon size={16} strokeWidth={1.5} />
                                    <span>{s.label}</span>
                                </a>
                            </li>
                        );
                    })}
                    {email && (
                        <li>
                            <a
                                href={`mailto:${email}`}
                                data-testid="footer-email"
                                className="inline-flex items-center gap-2 opacity-80 hover:opacity-100 hover:text-[color:var(--sc-accent)] transition-colors"
                            >
                                <Mail size={16} strokeWidth={1.5} />
                                <span>{email}</span>
                            </a>
                        </li>
                    )}
                </ul>
            </div>
        </footer>
    );
};

export default Footer;
